import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { usePrefersReducedMotion } from "@/hooks/useMotionPreferences";
import { GLOW_OPACITY, glowGeometry, glowPixelBudget, supportsWebGL2, type GlowGeometry } from "@/utils/releaseGlow";

export type ReleaseGlowMode = "css" | "shader";

const PulsingGlowCanvas = lazy(() => import("./PulsingGlowCanvas"));

class GlowBoundary extends React.Component<{ children: React.ReactNode; onFail: () => void }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error("[ReleaseGlow] Glow render failed:", error);
    this.props.onFail();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

interface ReleaseGlowProps {
  hostRef: RefObject<HTMLElement | null>;
  colors: string[];
  radius: number;
  onModeChange?: (mode: ReleaseGlowMode) => void;
}

interface HostBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

function isLowEndDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const cores = navigator.hardwareConcurrency ?? 8;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  return cores <= 4 || memory <= 4;
}

function sameBox(a: HostBox | null, b: HostBox): boolean {
  return Boolean(a && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5);
}

export function ReleaseGlow({ hostRef, colors, radius, onModeChange }: ReleaseGlowProps) {
  const reduced = usePrefersReducedMotion();
  const [inView, setInView] = useState(false);
  const [armed, setArmed] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [box, setBox] = useState<HostBox | null>(null);
  const boxRef = useRef<HostBox | null>(null);
  const [capable] = useState(() => supportsWebGL2());

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        setInView(visible);
        if (visible) setArmed(true);
      },
      { rootMargin: "160px 0px" },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, [hostRef]);

  const active = armed && capable && !failed;

  const mode: ReleaseGlowMode = active && ready ? "shader" : "css";

  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !active) return;
    let frame: number | null = null;
    const measure = () => {
      frame = null;
      const rect = host.getBoundingClientRect();
      const next = { left: rect.left + window.scrollX, top: rect.top + window.scrollY, width: rect.width, height: rect.height };
      if (sameBox(boxRef.current, next)) return;
      boxRef.current = next;
      setBox(next);
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };
    measure();
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    resizeObserver?.observe(host);
    resizeObserver?.observe(document.body);
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
    };
  }, [hostRef, active]);

  const geometry: GlowGeometry | null = useMemo(() => (box ? glowGeometry(box.width, box.height, radius) : null), [box?.width, box?.height, radius]);
  const markReady = useCallback(() => setReady(true), []);
  const budget = useMemo(() => glowPixelBudget(typeof window === "undefined" ? 1440 : window.innerWidth, isLowEndDevice()), []);

  if (!active || !box || !geometry || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="release-glow"
      aria-hidden="true"
      data-running={inView && !reduced ? "true" : "false"}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: box.top - geometry.bleed,
        height: geometry.canvasHeight,
        zIndex: 1,
        overflow: "hidden",
        pointerEvents: "none",
        opacity: GLOW_OPACITY,
      }}
    >
      <div style={{ position: "absolute", top: 0, left: box.left - geometry.bleed, width: geometry.canvasWidth, height: geometry.canvasHeight, contain: "strict" }}>
        <GlowBoundary onFail={() => setFailed(true)}>
          <Suspense fallback={null}>
            <PulsingGlowCanvas colors={colors} geometry={geometry} running={inView} reduced={reduced} maxPixelCount={budget} onReady={markReady} />
          </Suspense>
        </GlowBoundary>
      </div>
    </div>,
    document.body,
  );
}

export default ReleaseGlow;
